import { Box, CircularProgress, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import { useQuery } from "react-query";
import axiosClient from "../../../../lib/axios";
import IRequest from "../../../../types/IRequest";
import { ITarefas } from "../../../../types/api/bpm/pesquisar-tarefas";
import moment from "moment";
import TableCellPrinter from "../../../../components/TableCellPrinter";
import { useCallback, useEffect, useState } from "react";
import TagsSituacao from "../../../../components/TagsSituacao";
import { IDataProcessos } from "../../../../types/api/bpm/pesquisar-processos";
import { handleUpdateObjectState } from "../../../../helpers/handlersGenericos";
import { TFiltrosTarefas, THandleOpenDetailDialog } from "../..";
import { useLocation } from "react-router-dom";
import changeColorAnimation from "../colorAnimation";
import DataSemResultados from "../../../../components/DataSemResultados";
import HighlightedText from "../../../../components/HighlightSearch";


export default function ListaDisplay({
    keyword,
    filtros,
    handleOpenModalDetail
}: ListaDisplayProps) {

    const location = useLocation();
    const codigoParam = location.state?.codigo || null;

    const [codigo, setCodigo] = useState<string | null>(codigoParam)
    const [filtrosTarefas, setFiltrosTarefas] = useState<TFiltros>({
        pagina: 0,
        porPagina: 4
    })
    const [filtrosProcessos, setFiltrosProcessos] = useState<TFiltros>({
        pagina: 0,
        porPagina: 4
    })

    //queries e mutations
    const { data: dataTarefas, isLoading: isLoadingTarefas } = useQuery({
        queryKey: ["pesquisar-tarefas-para-fazer-lista", filtrosTarefas, keyword, filtros],
        queryFn: () => axiosClient.get("/pesquisar-tarefas?resolvida=0", {
            params: {
                pesquisar: keyword,
                limit: filtrosTarefas.porPagina,
                page: filtrosTarefas.pagina,
                ...(filtros && {
                    fluxo_contem: filtros.fluxo_contem?.map((item) => item.id) ?? null
                }),
            }
        }),
        cacheTime: 0
    });
    const { reqTarefasFazer, dataTarefasFazer }: ITarefasFazer = {
        reqTarefasFazer: dataTarefas?.data,
        dataTarefasFazer: dataTarefas?.data.data || []
    }

    const { data: dataProcessos, isLoading: isLoadingProcessos } = useQuery({
        queryKey: ["pesquisar-processos-criados", filtrosProcessos, keyword, filtros],
        queryFn: () => axiosClient.get("/pesquisar-processos", {
            params: {
                pesquisar: keyword,
                limit: filtrosProcessos.porPagina,
                page: filtrosProcessos.pagina,
                ...(filtros && {
                    fluxo_contem: filtros.fluxo_contem?.map((item) => item.id) ?? null
                }),
            }
        }),
        cacheTime: 0
    });
    const { reqProcessosCriados, dataProcessosCriados }: IProcessosCriados = {
        reqProcessosCriados: dataProcessos?.data,
        dataProcessosCriados: dataProcessos?.data.data || []
    }
    //

    //funcoes manipuladoras
    const handleChangePage = useCallback((_: React.MouseEvent<HTMLButtonElement> | null, novaPag: number, type: TTables) => {
        if (type === "Tarefas") handleUpdateObjectState("pagina", novaPag, setFiltrosTarefas)
        if (type === "Processos") handleUpdateObjectState("pagina", novaPag, setFiltrosProcessos)
    }, []);

    const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, type: TTables) => {
        const params = {
            porPagina: parseInt(event.target.value, 10),
            pagina: 1
        }
        if (type === "Tarefas") setFiltrosTarefas(params)
        if (type === "Processos") setFiltrosProcessos(params)
    }, []);
    //

    useEffect(() => {
        if (codigo) {
            const timeoutId = setTimeout(() => {
                setCodigo(null);
            }, 3200);

            return () => clearTimeout(timeoutId);
        }
    }, [codigo])

    return (
        <>
            <Box mt={2} component={Paper}>

                <Typography p={2} variant="h6">Tarefas para fazer</Typography>
                {
                    isLoadingTarefas &&
                    <Box display="flex" justifyContent="center" my={4}>
                        <CircularProgress />
                    </Box>
                }
                {
                    !isLoadingTarefas &&
                    <>
                        <TableContainer>
                            <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Processo</b></TableCell>
                                        <TableCell><b>Tarefa atual</b></TableCell>
                                        <TableCell><b>Criada por</b></TableCell>
                                        <TableCell><b>Criada em</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        dataTarefasFazer.length <= 0 &&
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <DataSemResultados texto="Nenhuma tarefa encontrada" />
                                            </TableCell>
                                        </TableRow>
                                    }

                                    {
                                        dataTarefasFazer.length > 0 && dataTarefasFazer.map((tarefa) => {
                                            const criadoEm = moment(tarefa.created_at).format('DD/MM/YYYY HH:mm')
                                            const processoCriadoEm = moment(tarefa.processo.created_at).format('DD/MM/YYYY HH:mm')

                                            return (
                                                <TableRow
                                                    key={tarefa.id}
                                                    sx={{
                                                        '&:last-child td, &:last-child th': { border: 0 }
                                                    }}
                                                >
                                                    <TableCell component="th" scope="row">
                                                        <Link onClick={() => handleOpenModalDetail(tarefa.processo.codigo)} component="div" sx={{ cursor: 'pointer' }}><HighlightedText text={`#    ${tarefa.processo.codigo}`} /></Link>
                                                        <Typography color="gray" variant='caption'>{processoCriadoEm}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TableCellPrinter
                                                            label={tarefa.titulo}
                                                            onlyLabel
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TableCellPrinter
                                                            label={tarefa.processo.usuario}
                                                            subLabel={tarefa.processo.perfil}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TableCellPrinter
                                                            label={criadoEm}
                                                            subLabel={tarefa.tempo_criacao}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={reqTarefasFazer?.total}
                            page={filtrosTarefas.pagina}
                            onPageChange={(e, number) => handleChangePage(e, number, "Tarefas")}
                            rowsPerPage={filtrosTarefas.porPagina}
                            onRowsPerPageChange={(e) => handleChangeRowsPerPage(e, "Tarefas")}
                            labelRowsPerPage="Linhas por página"
                            rowsPerPageOptions={[4, 8, 12]}
                        />
                    </>
                }
            </Box>
            <Box mt={2} component={Paper}>

                <Typography p={2} variant="h6">Processos criados</Typography>
                {
                    isLoadingProcessos &&
                    <Box display="flex" justifyContent="center" my={4}>
                        <CircularProgress />
                    </Box>
                }
                {
                    !isLoadingProcessos &&
                    <>
                        <TableContainer>
                            <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Processo</b></TableCell>
                                        <TableCell><b>Criado por</b></TableCell>
                                        <TableCell><b>Título</b></TableCell>
                                        <TableCell><b>Tarefa atual</b></TableCell>
                                        <TableCell><b>Situação</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        dataProcessosCriados.length <= 0 &&
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <DataSemResultados texto="Nenhum processo encontrado" />
                                            </TableCell>
                                        </TableRow>
                                    }

                                    {
                                        dataProcessosCriados.length > 0 && dataProcessosCriados.map((processo) => {
                                            const criadoEm = moment(processo.created_at).format('DD/MM/YYYY HH:mm')
                                            const processoCriado = processo.codigo === codigo

                                            return (
                                                <TableRow
                                                    key={processo.codigo}
                                                    sx={{
                                                        '&:last-child td, &:last-child th': { border: 0 },
                                                        ...(processoCriado && {
                                                            animation: `${changeColorAnimation} 3.2s forwards reverse`,
                                                        })
                                                    }}
                                                >
                                                    <TableCell component="th" scope="row">
                                                        <Link onClick={() => handleOpenModalDetail(processo.codigo)} component="div" sx={{ cursor: 'pointer' }}>
                                                        <HighlightedText text={`#${processo.codigo}`}/>
                                                        </Link>
                                                        <Typography color="gray" variant='caption'>{criadoEm}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TableCellPrinter
                                                            label={processo.usuario}
                                                            subLabel={processo.perfil}
                                                        />
                                                    </TableCell>
                                                    <TableCell><HighlightedText text={`${processo.titulo}`}/></TableCell>
                                                    <TableCell>
                                                        <TableCellPrinter
                                                            label={processo.tarefa_atual.titulo}
                                                            subLabel={processo.tarefa_atual.perfil}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TagsSituacao situacao={processo.situacao} />
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={reqProcessosCriados?.total}
                            page={filtrosProcessos.pagina}
                            onPageChange={(e, number) => handleChangePage(e, number, "Processos")}
                            rowsPerPage={filtrosProcessos.porPagina}
                            onRowsPerPageChange={(e) => handleChangeRowsPerPage(e, "Processos")}
                            labelRowsPerPage="Linhas por página"
                            rowsPerPageOptions={[4, 8, 12]}
                        />
                    </>
                }
            </Box>
        </>
    )
}



interface ListaDisplayProps {
    keyword: string,
    filtros: TFiltrosTarefas | null,
    handleOpenModalDetail: THandleOpenDetailDialog
}

interface ITarefasFazer {
    reqTarefasFazer: IRequest<ITarefas>,
    dataTarefasFazer: ITarefas[]
}

interface IProcessosCriados {
    reqProcessosCriados: IRequest<IDataProcessos>,
    dataProcessosCriados: IDataProcessos[]
}

type TFiltros = {
    pagina: number,
    porPagina: number
}

type TTables = "Tarefas" | "Processos"